/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { createOperationCallAndPoll, OperationHandler } from 'src/core/services/api/createOperationCall';
import { httpService } from 'src/core/services/api/httpService';
import { luisRequestOptionsBuilder } from 'src/core/utils/api/luisRequestOptionsBuilder';
import { OperationDelayStrategies } from 'src/core/utils/api/operationDelayStrategies';
import { OperationRetryStrategies } from 'src/core/utils/api/operationRetryStrategies';
import { urlBuilder } from 'src/core/utils/api/urlBuilder';
import { createService } from 'src/core/utils/createService';
import { FeatureToggles } from 'src/core/utils/featureToggles';
import { LuisConstants } from 'src/core/utils/LuisConstants';
import { TryoutModelVersion } from 'src/languageStudio/appShell/prebuiltDemos/utils/supportedModelVerion';
import {
    DetectedLanguagesDocuments,
    DetectedLanguagesSwagger,
    HealthcareJobStateSwagger,
    InputDocument,
    InputDocumentLanguage,
    KeyPhrasesDocuments,
    KeyPhrasesSwagger,
    LinkedEntitiesDocuments,
    LinkedEntitiesSwagger,
    NamedEntityRecognitionDocuments,
    NamedEntityRecognitionSwagger,
    PIIDocuments,
    PIISwagger,
    SentimentDocuments,
    SentimentSwagger,
    SummarizationJobStateSwagger,
    SummarizationSwagger,
    SummarizationTaskType
} from 'src/languageStudio/core/services/textAnalytics/textAnalyticsSwaggers';

/**
 * Represents a service for text analytics operations.
 */
export const getTextAnalyticsService = createService(({ context, getEnvironment }) => {
    const { apiUrl: languageTextAnalyticsBaseUrl } = getEnvironment({ apiName: 'languageTextAnalytics' });

    const apiOptions = luisRequestOptionsBuilder()
        .useDefaultOptions(context.serviceKey, true)
        .build();

    const getUrlBuilder = (apiVersion?: string) =>
        urlBuilder()
            .baseUrl(languageTextAnalyticsBaseUrl)
            .resource(':analyze-text')
            .queryParam('api-version', apiVersion ?? LuisConstants.Demos.ApiVersions.GA);

    const getLroUrlBuilder = (apiVersion?: string) =>
        urlBuilder()
            .baseUrl(languageTextAnalyticsBaseUrl)
            .resource('analyze-text')
            .resource('jobs')
            .queryParam('api-version', apiVersion ?? LuisConstants.Demos.ApiVersions.GA);

    /**
     * Gets a detailed sentiment analysis for the input text.
     * The analysis is done in multiple levels of granularity, start from the a document level,
     * down to sentence and key terms (targets) and assessments.
     *
     * @param param0.documents Input documents to be analyzed by the service.
     * @param param0.withAssessmentMining (Optional) if set to true, response will contain input
     * and document level statistics including target-based sentiment analysis results.
     */
    const getSentiment = async ({
        documents,
        withAssessmentMining = false
    }: {
        documents: InputDocument[];
        withAssessmentMining?: boolean;
    }): Promise<SentimentDocuments> => {
        const postUrl = getUrlBuilder().build();

        const reqBody = {
            kind: 'SentimentAnalysis',
            analysisInput: { documents },
            parameters: { opinionMining: FeatureToggles.SANewLanguageOMSupport ? true : withAssessmentMining }
        };

        const {
            data: { results: sentimentResult }
        } = await httpService.post<SentimentSwagger>(postUrl, reqBody, apiOptions);

        return sentimentResult;
    };

    /**
     * Gets a list of strings denoting the key phrases in the input text.
     * See the Supported languages in Text Analytics API for the list of enabled languages.
     *
     * @param param0.documents Input documents to be analyzed by the service.
     */
    const getKeyPhrases = async ({ documents }: { documents: InputDocument[] }): Promise<KeyPhrasesDocuments> => {
        const postUrl = getUrlBuilder().build();

        const reqBody = {
            kind: 'KeyPhraseExtraction',
            analysisInput: {
                documents
            }
        };

        const {
            data: { results: keyPhrasesResult }
        } = await httpService.post<KeyPhrasesSwagger>(postUrl, reqBody, apiOptions);

        return keyPhrasesResult;
    };

    /**
     * Gets a list of general named entities in a given document.
     * For the list of supported entity types, check Supported Entity Types in Text Analytics API
     * through this link https://docs.microsoft.com/en-us/azure/cognitive-services/text-analytics/named-entity-types?tabs=general.
     *
     * @param param0.documents Input documents to be analyzed by the service.
     */
    const getNamedEntityRecognition = async ({
        documents,
        modelVersion = 'GA'
    }: {
        documents: InputDocument[];
        modelVersion?: TryoutModelVersion;
    }): Promise<NamedEntityRecognitionDocuments> => {
        const postUrl = getUrlBuilder(modelVersion === 'Preview' ? '2022-10-01-preview' : null).build();

        const reqBody = {
            kind: 'EntityRecognition',
            analysisInput: {
                documents
            },
            parameters: {
                modelVersion: LuisConstants.Demos.ModelVersions[modelVersion]
            }
        };

        const {
            data: { results: nerResult }
        } = await httpService.post<NamedEntityRecognitionSwagger>(postUrl, reqBody, apiOptions);

        return nerResult;
    };

    /**
     * Gets a list of entities with personal information ("SSN", "Bank Account" etc) in the document.
     * For the list of supported entity types, check Supported Entity Types in Text Analytics API
     * through this link https://docs.microsoft.com/en-us/azure/cognitive-services/text-analytics/named-entity-types?tabs=general.
     *
     * @param param0.documents Input documents to be analyzed by the service.
     */
    const getPII = async ({ documents, withPhi }: { documents: InputDocument[]; withPhi?: boolean }): Promise<PIIDocuments> => {
        const postUrl = getUrlBuilder().build();

        const reqBody = {
            kind: 'PiiEntityRecognition',
            analysisInput: {
                documents
            },
            parameters: {
                domain: withPhi ? 'phi' : 'none'
            }
        };

        const {
            data: { results: piiResult }
        } = await httpService.post<PIISwagger>(postUrl, reqBody, apiOptions);

        return piiResult;
    };

    /**
     * Gets the detected language and a numeric score between 0 and 1.
     * Scores close to 1 indicate 100% certainty that the identified language is true.
     *
     * @param param0.documents Input documents to be analyzed by the service.
     */
    const detectLanguage = async ({ documents }: { documents: InputDocumentLanguage[] }): Promise<DetectedLanguagesDocuments> => {
        const postUrl = getUrlBuilder().build();

        const reqBody = {
            kind: 'LanguageDetection',
            analysisInput: {
                documents
            }
        };

        const {
            data: { results: languages }
        } = await httpService.post<DetectedLanguagesSwagger>(postUrl, reqBody, apiOptions);

        return languages;
    };

    /**
     * Gets a list of recognized entities with links to a well-known knowledge base.
     *
     * @param param0.documents Input documents to be analyzed by the service.
     */
    const getLinkedEntities = async ({ documents }: { documents: InputDocument[] }): Promise<LinkedEntitiesDocuments> => {
        const postUrl = getUrlBuilder().build();

        const reqBody = {
            kind: 'EntityLinking',
            analysisInput: {
                documents
            }
        };

        const {
            data: { results: linkedEntitiesResult }
        } = await httpService.post<LinkedEntitiesSwagger>(postUrl, reqBody, apiOptions);

        return linkedEntitiesResult;
    };

    /**
     * Start a healthcare analysis job to recognize healthcare related
     * entities (drugs, conditions, symptoms, etc) and their relations.
     *
     * @param param0.documents Input documents to be analyzed by the service.
     */
    const getHealthAnalysis = async ({
        documents,
        apiVersion,
        modelVersion,
        fhirStructure
    }: {
        documents: InputDocument[];
        fhirStructure?: boolean;
        apiVersion?: string;
        modelVersion?: string;
    }): Promise<HealthcareJobStateSwagger> => {
        const postUrl = getLroUrlBuilder(apiVersion).build();

        const reqBody = {
            tasks: [
                {
                    kind: 'Healthcare',
                    parameters: { modelVersion, fhirVersion: fhirStructure ? LuisConstants.Demos.TaForHealthFHIRVersion : null }
                }
            ],
            analysisInput: {
                documents
            }
        };

        const response = await createOperationCallAndPoll<HealthcareJobStateSwagger>(
            httpService.post(postUrl, reqBody, apiOptions),
            apiOptions,
            undefined,
            res => (<Record<string, string>>res.headers)[LuisConstants.OperationHeaders.LocationHeader]
        );

        return response.data;
    };

    /**
     * Gets a summary analysis for the input document.
     * The summary is returned in the shape of sentences.
     *
     * @param param0.documents Input documents to be analyzed by the service.
     * @param param0.sentenceCount (Optional) if added (value from 1 to 20, default 3),
     * it determines the number of returned sentences.
     * @param param0.query (Optional)if added tailors summary to query
     * @param param0.sortBy (Optional) if added (enum Offset or Rank, default Offset),
     * it determines whether the returned sentences are ordered by original order or relevance.
     */
    const getSummarization = async ({
        tasks,
        documents,
        sentenceCount,
        query,
        pollingCallHandler
    }: {
        query?: string;
        sentenceCount?: number;
        documents: InputDocument[];
        pollingCallHandler?: OperationHandler<SummarizationJobStateSwagger<SummarizationSwagger>>['handleCall'];
        tasks: SummarizationTaskType[];
    }): Promise<SummarizationJobStateSwagger<SummarizationSwagger>> => {
        const postUrl = urlBuilder()
            .baseUrl(languageTextAnalyticsBaseUrl)
            .resource('analyze-text')
            .resource('jobs')
            .queryParam('api-version', LuisConstants.Demos.SummarizationApiVersion)
            .build();

        // support for summary length in the abstractive summarization request
        const getParameters = (kind: SummarizationTaskType) => {
            let summaryLength = null;
            if (kind === 'AbstractiveSummarization' && sentenceCount) {
                if (sentenceCount <= 3) {
                    summaryLength = 'short';
                } else if (sentenceCount <= 6) {
                    summaryLength = 'medium';
                } else {
                    summaryLength = 'long';
                }
            }
            const parameters = {
                    ...(kind === 'AbstractiveSummarization' && summaryLength && { summaryLength }),
                    ...(kind === 'ExtractiveSummarization' && sentenceCount && { sentenceCount }),
                    ...(query && { query })
                };
            return parameters;
        };

        const payloadTasks = tasks.map(taskKind => ({ kind: taskKind, parameters: getParameters(taskKind) }));

        const response = await createOperationCallAndPoll<SummarizationJobStateSwagger<SummarizationSwagger>>(
            httpService.post<SummarizationSwagger>(postUrl, { analysisInput: { documents }, tasks: payloadTasks }, apiOptions),
            apiOptions,
            {
                handleCall: pollingCallHandler,
                retryStrategy: OperationRetryStrategies.defaultRetryStrategy,
                timeToDelay: OperationDelayStrategies.exponentialDelayStrategy
            },
            res => (<Record<string, string>>res.headers)[LuisConstants.OperationHeaders.LocationHeader]
        );

        return response.data;
    };

    return {
        getPII,
        getSentiment,
        getKeyPhrases,
        detectLanguage,
        getSummarization,
        getHealthAnalysis,
        getLinkedEntities,
        getNamedEntityRecognition
    };
});
